import { ProductSessionId } from '@boardflight/shared';
import { YEAR_SECONDS, dropAnalyticsCookie, readAnalyticsCookie, writeAnalyticsCookie } from './analytics-cookie';

/**
 * The two random ids product analytics keeps about a browser: the anonymous visitor, and the usage
 * session its events are grouped in.
 *
 * Each lives in a cookie both hosts of the site read (`analytics-cookie.ts`), so a visit that goes
 * from the library to the creator dashboard is one visitor in one session. Where the cookie cannot
 * be kept, this origin's `localStorage` holds it instead — which is also where both were kept
 * before the cookies, so an id found there is carried into the cookie the first time it is used.
 */

const KEY = 'boardflight.analytics.session';
const COOKIE = 'bf_asid';
const IDLE = 30 * 60_000;
const MAXIMUM = 24 * 60 * 60_000;

const VISITOR_KEY = 'boardflight.analytics.visitor';
const VISITOR_COOKIE = 'bf_anon';
const VISITOR = /^anon:[0-9a-f-]{36}$/;

/** PostHog requires a UUIDv7 whose timestamp is the session's start. */
function newSessionId(now: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  let timestamp = BigInt(now);
  for (let i = 5; i >= 0; i--) {
    bytes[i] = Number(timestamp & 255n);
    timestamp >>= 8n;
  }
  bytes[6] = (bytes[6]! & 15) | 112;
  bytes[8] = (bytes[8]! & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

interface SessionRecord { id: string; startedAt: number; lastSeenAt: number }

function current(saved: unknown, now: number): SessionRecord | undefined {
  const record = saved as Partial<SessionRecord> | null;
  return record && ProductSessionId.safeParse(record.id).success &&
    typeof record.startedAt === 'number' && typeof record.lastSeenAt === 'number' &&
    record.startedAt <= record.lastSeenAt && record.lastSeenAt <= now &&
    now - record.lastSeenAt < IDLE && now - record.startedAt < MAXIMUM ? record as SessionRecord : undefined;
}

/** `<id>.<startedAt>.<lastSeenAt>`: a cookie value with nothing in it to escape. */
function fromCookie(value: string | null): unknown {
  const [id, startedAt, lastSeenAt] = (value ?? '').split('.');
  return id ? { id, startedAt: Number(startedAt), lastSeenAt: Number(lastSeenAt) } : null;
}

function fromStorage(): unknown {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null');
  } catch {
    return null;
  }
}

export function clearAnalyticsSession(): void {
  dropAnalyticsCookie(COOKIE);
  try { localStorage.removeItem(KEY); } catch { /* No capture without storage. */ }
}

/** Shared by tabs and by both hosts. Only explicit captured browser events extend the session. */
export function analyticsSessionId(): string | undefined {
  try {
    const now = Date.now();
    const session = current(fromCookie(readAnalyticsCookie(COOKIE)), now) ?? current(fromStorage(), now) ??
      { id: newSessionId(now), startedAt: now, lastSeenAt: now };
    session.lastSeenAt = now;
    // The cookie lapses when the session would: thirty minutes after the last event.
    if (writeAnalyticsCookie(COOKIE, `${session.id}.${session.startedAt}.${session.lastSeenAt}`, IDLE / 1000)) {
      try { localStorage.removeItem(KEY); } catch { /* The cookie is the record. */ }
    } else {
      localStorage.setItem(KEY, JSON.stringify(session));
    }
    return session.id;
  } catch { return undefined; }
}

/**
 * The anonymous visitor this browser already is, or null — never one made up here. What a sign-in
 * joins to the account (`$identify`): a browser with no anonymous history has nothing to join.
 */
export function knownAnalyticsVisitor(): string | null {
  const cookie = readAnalyticsCookie(VISITOR_COOKIE);
  if (cookie && VISITOR.test(`anon:${cookie}`)) return `anon:${cookie}`;
  try {
    const stored = localStorage.getItem(VISITOR_KEY);
    return stored && VISITOR.test(stored) ? stored : null;
  } catch { return null; }
}

/**
 * The anonymous visitor, made on first use. Null where nothing can be kept, which sends nothing:
 * an id that changed on every event would count one person as many.
 */
export function analyticsVisitorId(): string | null {
  try {
    const id = knownAnalyticsVisitor() ?? `anon:${crypto.randomUUID()}`;
    if (writeAnalyticsCookie(VISITOR_COOKIE, id.slice('anon:'.length), YEAR_SECONDS)) {
      try { localStorage.removeItem(VISITOR_KEY); } catch { /* The cookie is the record. */ }
    } else {
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch { return null; }
}

/** End the anonymous identity, on both hosts: at sign-out, an account switch, and any change of the choice. */
export function forgetAnalyticsVisitor(): void {
  dropAnalyticsCookie(VISITOR_COOKIE);
  try { localStorage.removeItem(VISITOR_KEY); } catch { /* No capture without storage. */ }
}
